import React, { FC, memo, useCallback } from 'react';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { Suggestion, SuggestionList } from '@fluentai/suggestions';

const useChatSuggestionStyles = makeStyles({
  suggestionList: {
    marginTop: tokens.spacingVerticalL,
  },
  suggestion: {
    backgroundColor: tokens.colorTransparentBackground,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    justifyContent: 'left',
    display: 'inline-block',
  },
  suggestionWrap: {
    backgroundColor: '#ffffff',
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    justifyContent: 'left',
    display: 'inline-block',
    maxWidth: '75%',
    textAlign: 'left',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#e0e0e0',
    borderRadius: '16px',
    outlineStyle: 'none',
    fontFamily: '"Segoe UI", SegoeUI, "Helvetica Neue", Helvetica, Arial, sans-serif',
    fontSize: '12px',
    lineHeight: '16px',
    color: '#242424',
    cursor: 'pointer',
    transitionProperty: 'background',
    transitionDuration: '0.15s',
    ':hover': {
      backgroundColor: '#f5f5f5',
    },
    ':focus': {
      outlineStyle: 'none',
      boxShadow: `0 0 0 2px ${tokens.colorStrokeFocus2}`,
    },
    ':focus-visible': {
      outlineStyle: 'none',
      boxShadow: `0 0 0 2px ${tokens.colorStrokeFocus2}`,
    },
    ':focus:not(:focus-visible)': {
      boxShadow: 'none',
    },
    '& > *': {
      whiteSpace: 'normal',
      overflow: 'visible',
      textOverflow: 'clip',
      outlineStyle: 'none',
    },
    '&::after': {
      display: 'none',
    },
  },
});

export interface IChatSuggestion {
  Text: string;
  Id: string;
}

interface IChatSuggestionListProps {
  chatSuggestions?: IChatSuggestion[];
  onClickChatSuggestion: (chatSuggestion: IChatSuggestion) => void;
  wrapText?: boolean;
  contactSupportText?: string;
  onContactSupportClick?: () => void;
}

export const ChatSuggestionList: FC<IChatSuggestionListProps> = memo(({
  chatSuggestions = [],
  onClickChatSuggestion,
  wrapText = false,
  contactSupportText,
  onContactSupportClick,
}) => {
  const styles = useChatSuggestionStyles();

  const onClick = useCallback((chatSuggestion: IChatSuggestion) => {
    onClickChatSuggestion(chatSuggestion);
  }, [onClickChatSuggestion]);

  const wrapInlineStyle = wrapText ? {
    outline: 'none',
    boxShadow: 'none',
    border: '1px solid #e0e0e0',
    borderRadius: '16px',
    background: '#ffffff',
    fontFamily: '"Segoe UI", SegoeUI, "Helvetica Neue", Helvetica, Arial, sans-serif',
    fontSize: '12px',
    lineHeight: '16px',
    color: '#242424',
  } : undefined;

  return (
    <SuggestionList
      className={mergeClasses(styles.suggestionList, 'ads-copilot-chat-suggestion-list')}
    >
      {chatSuggestions?.map(chatSuggestion => (
        <Suggestion
          key={chatSuggestion.Id}
          className={mergeClasses(wrapText ? styles.suggestionWrap : styles.suggestion, 'ads-copilot-chat-suggestion')}
          style={wrapInlineStyle}
          onClick={() => onClick(chatSuggestion)}
        >
          <span title={chatSuggestion.Text}>
            {chatSuggestion.Text}
          </span>
        </Suggestion>
      ))}
      {contactSupportText && onContactSupportClick && (
        <Suggestion
          className={mergeClasses(wrapText ? styles.suggestionWrap : styles.suggestion, 'ads-copilot-chat-suggestion')}
          style={wrapInlineStyle}
          aria-label={contactSupportText}
          onClick={onContactSupportClick}
        >
          <span>{contactSupportText}</span>
        </Suggestion>
      )}
    </SuggestionList>
  );
});

ChatSuggestionList.defaultProps = {
  chatSuggestions: undefined,
  onClickChatSuggestion: undefined,
};
